import { type Brouillon, INCERTAINS_MAX } from "./brouillon-texte";

/**
 * La sauvegarde automatique d'un texte de la console, sans React : l'ecran s'y abonne
 * (`useSyncExternalStore`), les tests la pilotent avec une horloge simulee. Nee pour le texte
 * d'une double des Histoires (maquette `HistoiresEtats.dc.html`, « La sauvegarde, pas à pas »),
 * elle vit ici depuis que les livres-jeux (#276) s'en servent pour la trame et le texte d'une
 * scene : un module n'importe jamais un autre module. Elle ne connait que ce qu'une ecriture de
 * texte rend : la version, `P` (qui a ecrit, dans les mots du module : la provenance d'une
 * double, l'origine d'une scene) et, sur un 409, le texte courant.
 *
 * - **Une seconde apres la derniere touche**, le texte part avec la version qu'il remplace ; en
 *   quittant la zone de texte ou la double, il part tout de suite.
 * - **Une ecriture a la fois.** Une saisie tapee pendant l'envoi part apres lui, avec la version
 *   qu'il a rendue : jamais deux ecritures sur la meme version, qui feraient un faux 409.
 * - **Rien ne se perd.** Chaque saisie non confirmee est gardee dans le brouillon local ; il
 *   s'efface quand la console a exactement ce texte, et seulement s'il le garde encore (un autre
 *   onglet a pu y poser sa saisie).
 * - **Hors ligne, Lab a l'arret, console en panne** : nouvel essai toutes les 10 s, sans rien
 *   perdre. **409** : plus aucun envoi, jusqu'a « Recharger ».
 * - **Notre propre ecriture n'est pas un conflit.** Un envoi dont la reponse s'est perdue
 *   (reseau coupe, delai de 30 s, 5xx du frontal) a pu etre enregistre : son texte est retenu,
 *   et un 409 qui rend exactement ce texte, ecrit par Nicolas, en prend la version et renvoie la
 *   saisie en cours dessus. Tous les essais d'avant la confirmation partent sur la meme version :
 *   la console n'a pu en prendre qu'un, le premier arrive, et c'est presque toujours le premier
 *   parti (le reseau tombe pendant qu'il part, les suivants ne sortent pas). Le premier est donc
 *   toujours garde, en memoire comme au brouillon, avec les derniers. Tant qu'un de ces textes
 *   est la, rien n'est sur : meme revenue au texte d'avant, la zone repart.
 * - **Le depart de la page.** L'envoi qui part avec la page (`keepalive`) compte parmi ces textes
 *   des son depart, au brouillon : la page peut mourir avant sa reponse. L'ecran parti, son moteur
 *   n'ecrit plus au brouillon que s'il y trouve encore sa propre derniere saisie : l'ecran rouvert
 *   sur la double a pu y poser la sienne, ou laisser le brouillon.
 * - **Les autres ecritures de l'ecran ne passent jamais par-dessus une saisie.** Restaurer,
 *   repartir de zero ou un brouillon a reprendre tiennent la zone (`verrouiller`) : la frappe y
 *   est ignoree. « Repartir de zéro » vide d'abord la saisie en attente (`vider`), et `remplacer`
 *   refuse tant qu'une saisie n'est pas enregistree ou qu'un envoi est en vol.
 * - **Un 409 fige tout.** Pendant un 409, la reponse d'un envoi parti avant lui ne change plus
 *   l'etat : la zone reste figee, avec « Recharger », jusqu'a ce qu'on recharge.
 */

/** Une ecriture confirmee par la console. */
export type TexteConfirme<P> = {
  readonly texte_version: number;
  readonly provenance: P | null;
  readonly maj_le: string;
};

/** Le texte courant, rendu avec un 409 : qui l'a ecrit en dernier dit si c'est notre ecriture. */
export type TexteCourant<P> = {
  readonly texte: string | null;
  readonly provenance: P | null;
  readonly texte_version: number;
  readonly derniere_origine: "nicolas" | "claude" | null;
};

/** Ce que rend une ecriture de texte. Aucune ne leve : l'ecran decide de la suite. */
export type ResultatEcritureTexte<P> =
  | { readonly type: "ok"; readonly reponse: TexteConfirme<P> }
  /** 409 : le texte a change ailleurs, et voici le texte courant. */
  | { readonly type: "conflit"; readonly courant: TexteCourant<P> }
  /** La requete n'est pas partie : le reseau est coupe. */
  | { readonly type: "hors_ligne" }
  /** 503 : le Lab est a l'arret (`stop_all`) ou la console en pause. */
  | { readonly type: "arret" }
  /** 401 ou 403 : la session a pris fin, ou l'adresse n'est plus sur la liste. */
  | { readonly type: "session"; readonly statut: number }
  /** Tout autre refus, avec le message de la console et son code quand le corps en a un. */
  | {
      readonly type: "refus";
      readonly statut: number;
      readonly message: string;
      readonly code?: string | undefined;
    };

/** Les fins de ligne de Windows et des vieux Mac deviennent `\n` : la zone de texte n'en a pas d'autres. */
export function normaliser(texte: string | null | undefined): string {
  return (texte ?? "").replace(/\r\n?/g, "\n");
}

/** Au plus autant de tours pour vider la saisie en attente (chaque tour envoie ou attend). */
const VIDANGES_MAX = 10;

/** Au plus autant de textes partis sans reponse gardes en memoire, le premier compris. */
const INCERTAINS_EN_MEMOIRE = 50;

/**
 * Le premier texte et les derniers, au plus `max` en tout : le premier est celui que la console a
 * le plus de chances d'avoir enregistre.
 */
function premierEtDerniers(textes: readonly string[], max: number): string[] {
  if (textes.length <= max) return [...textes];
  return [...textes.slice(0, 1), ...textes.slice(textes.length - (max - 1))];
}

export const DELAI_SAUVEGARDE_MS = 1000;
export const NOUVEL_ESSAI_MS = 10_000;

export type EtatSauvegarde<P> =
  /** Rien a enregistrer, rien d'enregistre depuis l'ouverture. */
  | { readonly type: "repos" }
  /** Une saisie attend sa seconde de calme. */
  | { readonly type: "modifie" }
  | { readonly type: "envoi" }
  | { readonly type: "enregistre"; readonly le: number }
  | { readonly type: "hors_ligne"; readonly prochainEssai: number }
  | { readonly type: "arret"; readonly prochainEssai: number }
  | { readonly type: "session"; readonly statut: number }
  /** Un refus de la console ; nouvel essai seulement quand elle etait en panne (5xx). */
  | {
      readonly type: "refus";
      readonly message: string;
      readonly prochainEssai: number | null;
    }
  | { readonly type: "conflit"; readonly courant: TexteCourant<P> };

export type Instantane<P> = {
  /** Le texte de la zone de saisie. */
  readonly texte: string;
  /** Le dernier texte que la console a confirme. */
  readonly enregistre: string;
  /** La version sur laquelle la prochaine ecriture s'appuie. */
  readonly version: number;
  readonly provenance: P | null;
  readonly etat: EtatSauvegarde<P>;
  /** Vrai quand le texte affiche vient d'etre relu parce qu'il avait change ailleurs. */
  readonly relu: boolean;
  /**
   * Vrai quand une autre ecriture de l'ecran tient la zone (restauration, effacement et sa
   * confirmation, brouillon a reprendre) : la zone est figee, la frappe ignoree.
   */
  readonly verrouille: boolean;
  /** Les raisons qui tiennent la zone, dans l'ordre ou elles l'ont prise. */
  readonly verrous: readonly string[];
};

export type Dependances<P> = {
  readonly ecrire: (
    texte: string,
    version: number,
    keepalive: boolean,
  ) => Promise<ResultatEcritureTexte<P>>;
  readonly brouillon: {
    readonly ecrire: (brouillon: Brouillon) => void;
    /** Efface le brouillon, seulement s'il garde encore exactement ce texte. */
    readonly effacer: (texte: string) => void;
    /**
     * Le brouillon garde en ce moment, ou `null` : un moteur demonte le relit avant d'y ecrire,
     * pour ne jamais remplacer celui de l'ecran rouvert sur la double.
     */
    readonly lire: () => Brouillon | null;
  };
  /** Apres chaque ecriture confirmee : le cache de la double suit. */
  readonly apresSauvegarde?: (texte: string, reponse: TexteConfirme<P>) => void;
  /**
   * Apres un 409 reconnu comme notre propre ecriture : le cache de la double prend le texte, la
   * version et la provenance que la console a rendus.
   */
  readonly apresRattrapage?: (courant: TexteCourant<P>) => void;
  /** En fin de seance d'ecriture (la zone quittee, tout enregistre) : relire le journal. */
  readonly relire?: () => void;
  readonly maintenant?: () => number;
};

export type Moteur<P> = {
  readonly lire: () => Instantane<P>;
  readonly abonner: (prevenir: () => void) => () => void;
  /** Une frappe. Ignoree pendant un 409 (la zone est figee). */
  readonly saisir: (texte: string) => void;
  /** La zone de texte perd le focus : envoi immediat, puis relecture du journal. */
  readonly quitter: () => void;
  /** Envoie tout de suite ce qui n'est pas enregistre. */
  readonly envoyer: (keepalive?: boolean) => Promise<void>;
  readonly reessayer: () => void;
  /**
   * « Reprendre ma saisie » : le brouillon repart avec la version sur laquelle il a ete tape, et
   * les textes partis sans reponse qu'il garde. Un brouillon que la console a deja (une relecture
   * l'a rendu) s'efface sans rien renvoyer : la version ne recule jamais.
   */
  readonly reprendre: (brouillon: Brouillon) => void;
  /** « Recharger » apres un 409 : le texte courant de la console prend la place. */
  readonly recharger: (courant: TexteCourant<P>) => void;
  /**
   * Un texte pose par la console (repartir de zero, restaurer), deja enregistre. Refuse, et
   * garde la saisie, tant qu'elle n'est pas enregistree ou qu'un envoi est en vol : la prochaine
   * ecriture fera alors le 409, qui la garde visible et copiable.
   */
  readonly remplacer: (texte: string, version: number, provenance: P | null) => boolean;
  /**
   * Une relecture rend une version plus recente : prise seulement si rien n'attend d'etre
   * enregistre. Sinon la prochaine ecriture fera le 409, qui garde la saisie.
   */
  readonly adopter: (texte: string, version: number, provenance: P | null) => boolean;
  /** Un 409 venu d'une autre ecriture de l'ecran (repartir de zero, restaurer). */
  readonly signalerConflit: (courant: TexteCourant<P>) => void;
  /**
   * Vrai tant qu'une saisie n'est pas confirmee, qu'un envoi est en cours, ou qu'un envoi parti
   * sans reponse n'a pas ete tire au clair.
   */
  readonly occupe: () => boolean;
  /** Une ecriture de l'ecran prend la zone, pour la raison dite ; `deverrouiller` la rend. */
  readonly verrouiller: (raison: string) => void;
  readonly deverrouiller: (raison: string) => void;
  /**
   * Envoie la saisie en attente et attend l'envoi en vol : vrai quand la console a exactement
   * le texte de la zone, faux quand une ecriture a echoue ou qu'un 409 l'a arretee.
   */
  readonly vider: () => Promise<boolean>;
  readonly monter: () => void;
  /** L'ecran part : minuteries arretees, derniere saisie envoyee (elle reste au brouillon). */
  readonly demonter: () => void;
};

export function creerMoteur<P>(
  depart: {
    readonly texte: string;
    readonly version: number;
    readonly provenance: P | null;
  },
  dependances: Dependances<P>,
): Moteur<P> {
  // Lue a chaque appel, jamais retenue : une horloge remplacee apres la creation fait foi.
  const maintenant = dependances.maintenant ?? (() => Date.now());
  let instantane: Instantane<P> = {
    texte: depart.texte,
    enregistre: depart.texte,
    version: depart.version,
    provenance: depart.provenance,
    etat: { type: "repos" },
    relu: false,
    verrouille: false,
    verrous: [],
  };
  const abonnes = new Set<() => void>();
  let minuterie: ReturnType<typeof setTimeout> | null = null;
  let essai: ReturnType<typeof setTimeout> | null = null;
  let enVol = false;
  /** Le texte de l'envoi en cours. */
  let enVolTexte: string | null = null;
  /** L'envoi en cours, resolu une fois sa reponse traitee : `vider` l'attend. */
  let enVolPromesse: Promise<void> | null = null;
  /** Les ecritures de l'ecran qui tiennent la zone. */
  const verrous = new Set<string>();
  let aRelancer = false;
  let conflit = false;
  let demonte = false;
  let relireApres = false;
  let nonRelues = 0;
  /**
   * Les textes partis dont la reponse s'est perdue, dans l'ordre de leur premier depart : la
   * console a pu en enregistrer un, le premier arrive.
   */
  let incertains: string[] = [];
  /** Le dernier texte que ce moteur a pose au brouillon, et qu'il peut donc en effacer. */
  let dernierGarde: string | null = null;

  const poser = (changement: Partial<Instantane<P>>): void => {
    instantane = { ...instantane, ...changement };
    for (const prevenir of abonnes) prevenir();
  };

  /**
   * Vrai quand quelque chose reste a envoyer : une saisie que la console n'a pas confirmee, ou
   * un envoi parti sans reponse, que seul un nouvel envoi tirera au clair (la zone a pu revenir
   * au texte d'avant pendant que la console prenait l'envoi perdu).
   */
  const aEnvoyer = (): boolean =>
    instantane.texte !== instantane.enregistre || incertains.length > 0;

  const arreterLaMinuterie = (): void => {
    if (minuterie !== null) clearTimeout(minuterie);
    minuterie = null;
  };

  const arreterLEssai = (): void => {
    if (essai !== null) clearTimeout(essai);
    essai = null;
  };

  const garder = (texte: string, version: number): void => {
    // Un moteur demonte (la double quittee) n'ecrit plus que par-dessus sa propre derniere saisie :
    // l'ecran rouvert sur la double a pu poser la sienne, ou laisser le brouillon, depuis. Ses
    // reponses tardives (rattrapage, confirmation, echec) ne le remplacent jamais.
    if (demonte && (dependances.brouillon.lire()?.texte ?? null) !== dernierGarde) return;
    // L'envoi en cours compte parmi les incertains : si la page se ferme avant sa reponse, le
    // brouillon repris saura que la console l'a peut-etre deja.
    const enAttente = [...incertains];
    if (enVol && enVolTexte !== null && !enAttente.includes(enVolTexte)) enAttente.push(enVolTexte);
    dernierGarde = texte;
    dependances.brouillon.ecrire({
      texte,
      version,
      le: new Date(maintenant()).toISOString(),
      ...(enAttente.length > 0 ? { incertains: premierEtDerniers(enAttente, INCERTAINS_MAX) } : {}),
    });
  };

  /** Le brouillon de ce moteur n'a plus lieu d'etre : la console a le texte, ou la zone y est revenue. */
  const oublierLeBrouillon = (texte: string): void => {
    dependances.brouillon.effacer(texte);
    dernierGarde = null;
  };

  /**
   * Un envoi parti sans reponse. Deja retenu, il garde sa place : c'est l'ordre des premiers
   * departs qui compte. En memoire, le premier reste toujours, avec les derniers ; le brouillon
   * suit tout de suite, pour une page qui mourrait avant la frappe suivante.
   */
  const retenirIncertain = (texte: string): void => {
    if (!incertains.includes(texte)) {
      incertains = premierEtDerniers([...incertains, texte], INCERTAINS_EN_MEMOIRE);
    }
    // Demonte, `garder` n'ecrit que si le brouillon est encore le sien.
    garder(instantane.texte, instantane.version);
  };

  const relire = (): void => {
    relireApres = false;
    if (nonRelues === 0) return;
    nonRelues = 0;
    dependances.relire?.();
  };

  const planifier = (): void => {
    arreterLaMinuterie();
    minuterie = setTimeout(() => {
      minuterie = null;
      void envoyer(false);
    }, DELAI_SAUVEGARDE_MS);
  };

  const planifierUnEssai = (): number | null => {
    arreterLEssai();
    if (demonte) return null;
    essai = setTimeout(() => {
      essai = null;
      void envoyer(false);
    }, NOUVEL_ESSAI_MS);
    return maintenant() + NOUVEL_ESSAI_MS;
  };

  const auRepos = (): EtatSauvegarde<P> => {
    const { etat } = instantane;
    if (etat.type === "enregistre") return etat;
    return { type: "repos" };
  };

  /**
   * Un 409 qui rend exactement un texte parti sans reponse, ecrit par Nicolas : c'est notre
   * propre ecriture, que la console avait enregistree. Sa version est prise, et la saisie tapee
   * depuis repart dessus.
   */
  const estNotreEcriture = (courant: TexteCourant<P>): boolean =>
    courant.derniere_origine === "nicolas" &&
    courant.texte !== null &&
    incertains.includes(normaliser(courant.texte));

  const rattraper = (courant: TexteCourant<P>, keepalive: boolean): void => {
    const texte = normaliser(courant.texte);
    incertains = [];
    nonRelues += 1;
    poser({ enregistre: texte, version: courant.texte_version, provenance: courant.provenance });
    // Le cache de la double suit, comme apres une ecriture confirmee : un retour sur la double
    // repart de la version que la console a rendue.
    dependances.apresRattrapage?.(courant);
    if (instantane.texte === texte) {
      aRelancer = false;
      poser({ etat: { type: "enregistre", le: maintenant() } });
      oublierLeBrouillon(texte);
      if (relireApres) relire();
      return;
    }
    garder(instantane.texte, courant.texte_version);
    poser({ etat: { type: "modifie" } });
    aRelancer = false;
    void envoyer(keepalive || demonte);
  };

  /** Une ecriture confirmee : la console a exactement `envoye`, a la version rendue. */
  const confirmer = (reponse: TexteConfirme<P>, envoye: string, keepalive: boolean): void => {
    const encore = instantane.texte !== envoye;
    incertains = [];
    nonRelues += 1;
    poser({
      enregistre: envoye,
      version: reponse.texte_version,
      provenance: reponse.provenance,
      // Un 409 signale par l'ecran pendant l'envoi reste affiche : la zone reste figee
      // jusqu'a « Recharger », la console a simplement ce texte de plus.
      ...(conflit
        ? {}
        : { etat: encore ? { type: "modifie" } : { type: "enregistre", le: maintenant() } }),
    });
    dependances.apresSauvegarde?.(envoye, reponse);
    if (encore) {
      // La saisie tapee pendant l'envoi s'appuie desormais sur la version rendue.
      garder(instantane.texte, reponse.texte_version);
      if (conflit) {
        aRelancer = false;
        return;
      }
      if (aRelancer || demonte) {
        aRelancer = false;
        void envoyer(keepalive || demonte);
      } else if (minuterie === null) {
        planifier();
      }
      return;
    }
    aRelancer = false;
    oublierLeBrouillon(envoye);
    if (relireApres) relire();
  };

  const traiter = (
    resultat: ResultatEcritureTexte<P>,
    envoye: string,
    keepalive: boolean,
  ): void => {
    if (conflit) {
      // En plein 409, seule une ecriture passee se note (la console a ce texte de plus) ; rien
      // d'autre ne change l'etat, ni ne se prend pour notre propre ecriture : la zone reste
      // figee, avec « Recharger ».
      aRelancer = false;
      if (resultat.type === "ok") confirmer(resultat.reponse, envoye, keepalive);
      return;
    }
    switch (resultat.type) {
      case "ok":
        confirmer(resultat.reponse, envoye, keepalive);
        return;
      case "conflit":
        if (estNotreEcriture(resultat.courant)) {
          rattraper(resultat.courant, keepalive);
          return;
        }
        conflit = true;
        aRelancer = false;
        arreterLaMinuterie();
        arreterLEssai();
        poser({ etat: { type: "conflit", courant: resultat.courant } });
        return;
      case "hors_ligne":
      case "arret": {
        // Reseau coupe ou delai depasse : la console a peut-etre enregistre. Le Lab a l'arret
        // repond avant la route : rien n'est enregistre.
        if (resultat.type === "hors_ligne") retenirIncertain(envoye);
        aRelancer = false;
        const prochainEssai = planifierUnEssai() ?? maintenant() + NOUVEL_ESSAI_MS;
        poser({ etat: { type: resultat.type, prochainEssai } });
        return;
      }
      case "session":
        aRelancer = false;
        poser({ etat: { type: "session", statut: resultat.statut } });
        return;
      case "refus": {
        // Un 5xx du frontal (502, 504) peut couper une reponse que la console avait donnee.
        if (resultat.statut >= 500) retenirIncertain(envoye);
        aRelancer = false;
        const prochainEssai = resultat.statut >= 500 ? planifierUnEssai() : null;
        poser({ etat: { type: "refus", message: resultat.message, prochainEssai } });
        return;
      }
    }
  };

  /** Envoie la saisie ; la promesse se resout une fois la reponse traitee. */
  function envoyer(keepalive = false): Promise<void> {
    if (conflit) return Promise.resolve();
    if (enVol) {
      aRelancer = true;
      // La page part pendant un envoi : il entre au brouillon parmi les incertains, la page
      // peut mourir avant sa reponse.
      if (keepalive) garder(instantane.texte, instantane.version);
      return enVolPromesse ?? Promise.resolve();
    }
    arreterLaMinuterie();
    arreterLEssai();
    const envoye = instantane.texte;
    if (!aEnvoyer()) {
      if (relireApres) relire();
      return Promise.resolve();
    }
    enVol = true;
    enVolTexte = envoye;
    // L'envoi du depart (la page qui se ferme, la double quittee) entre au brouillon parmi les
    // incertains avant de partir : la page peut mourir avant sa reponse.
    if (keepalive) garder(envoye, instantane.version);
    poser({ etat: { type: "envoi" } });
    const envoi = dependances
      .ecrire(envoye, instantane.version, keepalive)
      // `ecrire` ne leve jamais ; si elle le faisait, l'envoi compte comme parti sans reponse.
      .catch((): ResultatEcritureTexte<P> => ({ type: "hors_ligne" }))
      .then((resultat) => {
        enVol = false;
        enVolTexte = null;
        enVolPromesse = null;
        traiter(resultat, envoye, keepalive);
      });
    enVolPromesse = envoi;
    return envoi;
  }

  const vider = async (): Promise<boolean> => {
    for (let tour = 0; tour < VIDANGES_MAX; tour += 1) {
      if (conflit) return false;
      if (enVolPromesse !== null) {
        await enVolPromesse;
        continue;
      }
      if (!aEnvoyer()) return true;
      await envoyer(false);
      if (conflit) return false;
      // L'envoi a echoue (hors ligne, arret, session, refus) : rien de plus ne partira seul.
      const attend = enVolPromesse === null && aEnvoyer();
      if (attend && instantane.etat.type !== "modifie") return false;
    }
    return false;
  };

  const saisir = (texte: string): void => {
    if (conflit || verrous.size > 0 || texte === instantane.texte) return;
    // Revenue au texte enregistre, la zone repart tout de meme tant qu'un envoi perdu n'est pas
    // tire au clair : la console a peut-etre pris ce qu'on vient d'effacer.
    const modifie = texte !== instantane.enregistre || incertains.length > 0;
    if (modifie) garder(texte, instantane.version);
    else if (!enVol && dernierGarde !== null) oublierLeBrouillon(dernierGarde);
    const { etat } = instantane;
    // Hors ligne ou Lab a l'arret : le prochain essai enverra la derniere saisie, l'etat reste
    // affiche. Session perdue : rien ne part avant « Réessayer ».
    const enPanne = etat.type === "hors_ligne" || etat.type === "arret" || etat.type === "session";
    if (!modifie) {
      arreterLaMinuterie();
      if (!enVol) arreterLEssai();
      poser({ texte, relu: false, etat: enVol ? etat : auRepos() });
      return;
    }
    poser({
      texte,
      relu: false,
      etat: enVol || enPanne ? etat : { type: "modifie" },
    });
    if (enPanne) return;
    arreterLEssai();
    planifier();
  };

  return {
    lire: () => instantane,
    abonner: (prevenir) => {
      abonnes.add(prevenir);
      return () => {
        abonnes.delete(prevenir);
      };
    },
    saisir,
    quitter: () => {
      relireApres = true;
      void envoyer(false);
    },
    envoyer,
    reessayer: () => {
      void envoyer(false);
    },
    reprendre: (brouillon) => {
      if (conflit) return;
      // La console a deja ce texte (une relecture l'a rendu, en plus recent) : rien a renvoyer,
      // et la version ne recule jamais.
      if (brouillon.texte === instantane.enregistre && !aEnvoyer()) {
        oublierLeBrouillon(brouillon.texte);
        return;
      }
      incertains = [...(brouillon.incertains ?? [])];
      garder(brouillon.texte, brouillon.version);
      poser({ texte: brouillon.texte, version: brouillon.version, etat: { type: "modifie" } });
      void envoyer(false);
    },
    recharger: (courant) => {
      conflit = false;
      incertains = [];
      const texte = normaliser(courant.texte);
      poser({
        texte,
        enregistre: texte,
        version: courant.texte_version,
        provenance: courant.provenance,
        etat: { type: "repos" },
        relu: false,
      });
    },
    remplacer: (texte, version, provenance) => {
      if (enVol || aEnvoyer()) return false;
      arreterLaMinuterie();
      arreterLEssai();
      incertains = [];
      conflit = false;
      poser({
        texte,
        enregistre: texte,
        version,
        provenance,
        etat: { type: "enregistre", le: maintenant() },
        relu: false,
      });
      return true;
    },
    adopter: (texte, version, provenance) => {
      if (version <= instantane.version || aEnvoyer() || enVol || conflit) return false;
      poser({ texte, enregistre: texte, version, provenance, relu: true });
      return true;
    },
    signalerConflit: (courant) => {
      conflit = true;
      arreterLaMinuterie();
      arreterLEssai();
      poser({ etat: { type: "conflit", courant } });
    },
    occupe: () => enVol || aEnvoyer(),
    verrouiller: (raison) => {
      if (verrous.has(raison)) return;
      verrous.add(raison);
      poser({ verrouille: true, verrous: [...verrous] });
    },
    deverrouiller: (raison) => {
      if (!verrous.delete(raison)) return;
      poser({ verrouille: verrous.size > 0, verrous: [...verrous] });
    },
    vider,
    monter: () => {
      demonte = false;
    },
    demonter: () => {
      demonte = true;
      arreterLaMinuterie();
      arreterLEssai();
      if (!conflit && (enVol || aEnvoyer())) void envoyer(true);
    },
  };
}
